import React, { useMemo } from 'react';

import { Draggable } from '@hello-pangea/dnd';
import NaturalDragAnimation from 'natural-drag-animation-rbdnd';
import { useTranslation } from 'react-i18next';

import { ReactComponent as TrashIcon } from '@pkg/assets/icons/v2/trash.svg';
import { CircleButton, YatTile } from '@pkg/components';
import { useAppSelector } from '@pkg/hooks';

import { getGlowLevel, type GlowLevelTypes } from '@app/helpers';
import { type Emoji } from '@app/store/types/picker';

import './SelectedEmojisItem.scss';

interface SelectedEmojisItemProps {
    emoji: Emoji;
    onRemove?: () => void;
    index: number;
    id: string;
    glowEnabled?: boolean;
}

const SelectedEmojisItem = ({
    emoji,
    onRemove = () => {
        return;
    },
    index,
    id,
    glowEnabled = false,
}: SelectedEmojisItemProps) => {
    const { t } = useTranslation(['yat_picker']);
    const { rhythmScore } = useAppSelector((rootState) => rootState.picker);
    const generation = 1;

    const getDraggingStyle = (isDragging, draggableStyle) => {
        const boxShadow = isDragging
            ? { boxShadow: '0 0.3em 0.7em 0 rgba(7,9,25, 1)' }
            : { boxShadow: '0 0.3em 0.7em 0 rgba(7,9,25,0)' };

        return {
            ...boxShadow,
            ...draggableStyle,
        };
    };

    const className = useMemo(() => {
        const variant: GlowLevelTypes = getGlowLevel(rhythmScore);
        let className = 'picker-form-selected-emojis-item';
        if (glowEnabled) className += ` picker-form-selected-emojis-item--${variant}`;

        return className;
    }, [rhythmScore, glowEnabled]);

    return (
        <Draggable draggableId={id + ''} index={index}>
            {(provided, snapshot) => {
                return (
                    <NaturalDragAnimation
                        style={provided.draggableProps.style}
                        snapshot={snapshot}
                        animationRotationFade={0.75}
                        rotationMultiplier={4}
                    >
                        {(style) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={className}
                                style={getDraggingStyle(snapshot.isDragging, style)}
                            >
                                <YatTile
                                    yat={emoji[0]}
                                    generation={generation}
                                    rhythmScore={rhythmScore}
                                    showRhythmScore={false}
                                    singleEmoji={true}
                                />
                                <CircleButton
                                    ariaLabel={t('remove_emoji')}
                                    onClick={onRemove}
                                    classes="picker-form-selected-emojis-item__remove-button"
                                >
                                    <TrashIcon />
                                </CircleButton>
                            </div>
                        )}
                    </NaturalDragAnimation>
                );
            }}
        </Draggable>
    );
};

export default SelectedEmojisItem;
